import React, { useCallback, useEffect, useState } from 'react';
import { Film, Radio, RefreshCw, Trophy, Tv } from 'lucide-react';
import Loader from '../Loader';
import { Badge, Empty, HScroll } from './UpUi';
import { ago } from './format';

const KINDS = { film: { label: 'Film', icon: Film }, serie: { label: 'Série', icon: Tv }, sport: { label: 'Sport', icon: Trophy } };
const REFRESH_MS = 15000;

/**
 * Qui regarde quoi en ce moment (films, séries, sport en direct) : lectures signalées par les lecteurs du projet
 * (GET /watching). Plusieurs IP en même temps sur un compte → « Partage possible » (rien n'est coupé côté site).
 * Filtre « Plusieurs lectures » : seulement les comptes qui lisent plusieurs choses en même temps (les plus nombreux en
 * premier), pour repérer un compte prêté. Relu toutes les 15 s tant que l'onglet est ouvert.
 */
const UpWatching = ({ pApi, onOpen, reloadKey }) => {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [only, setOnly] = useState('') // '' | multi (plusieurs lectures) | shared (partage possible)
    const load = useCallback(async () => {
        try {
            setData(await pApi.get('/watching'))
            setError('')
        } catch (e) { setError(e.message) }
    }, [pApi])
    useEffect(() => {
        load()
        const t = setInterval(load, REFRESH_MS)
        return () => clearInterval(t)
    }, [load, reloadKey])

    const plays = data ? data.items.reduce((n, w) => n + w.plays.length, 0) : 0
    const shared = data ? data.items.filter((w) => w.sharing).length : 0
    const multi = data ? data.items.filter((w) => w.plays.length > 1).length : 0
    const items = !data
        ? []
        : only === 'multi'
            ? data.items.filter((w) => w.plays.length > 1).sort((a, b) => b.plays.length - a.plays.length)
            : only === 'shared'
                ? data.items.filter((w) => w.sharing)
                : data.items

    return (
        <div>
            <div className='up-toolbar'>
                <p className='up-count'>
                    <b>{data?.items.length ?? '…'}</b> compte{data?.items.length > 1 ? 's' : ''} en lecture · {plays} lecture{plays > 1 ? 's' : ''}
                    {shared > 0 && <> · <Badge tone='warn'>{shared} partage{shared > 1 ? 's' : ''} possible{shared > 1 ? 's' : ''}</Badge></>}
                </p>
                <select value={only} onChange={(e) => setOnly(e.target.value)} title='Filtrer les comptes'>
                    <option value=''>Tous les comptes</option>
                    <option value='multi'>Plusieurs lectures en même temps ({multi})</option>
                    <option value='shared'>Partage possible : plusieurs IP ({shared})</option>
                </select>
                <button className='btn cw up-icon-btn' onClick={load} title='Actualiser'><RefreshCw size={15} /></button>
            </div>
            {error && <p className='up-error'>{error}</p>}
            {!data && !error ? <div className='up-loading'><Loader /></div> : data && (
                <HScroll>
                    <div className='up-table up-table-watching'>
                        <div className='up-thead'><span>User</span><span>Regarde</span><span>Appareil · IP</span><span>Depuis</span></div>
                        {items.flatMap((w) =>
                            w.plays.map((p, i) => {
                                const K = KINDS[p.kind] || KINDS.film
                                return (
                                    <div key={`${w.userId}-${i}`} className={`up-row${w.sharing ? ' up-row-warn' : ''}`} onClick={() => onOpen(w.userId)}>
                                        <span className='up-user'>
                                            <span className='truncate' title={w.email}>{w.email}</span>
                                            {w.premium && <Badge tone='gold'>Premium</Badge>}
                                            {w.plays.length > 1 && i === 0 && <Badge tone='warn'>{w.plays.length} lectures</Badge>}
                                            {w.sharing && i === 0 && <Badge tone='warn'>Partage possible</Badge>}
                                        </span>
                                        <span className='up-user'>
                                            <Badge tone={p.kind === 'sport' ? 'live' : 'info'}><K.icon size={12} /> {K.label}</Badge>
                                            <span className='truncate' title={p.title}>{p.title}</span>
                                        </span>
                                        <span className='truncate'>{p.device || '—'} · <code>{p.ip || '?'}</code></span>
                                        <span><Radio size={12} /> {ago(p.since)}</span>
                                    </div>
                                )
                            }),
                        )}
                        {!items.length && <Empty>{data.items.length ? 'Aucun compte pour ce filtre' : 'Personne ne regarde en ce moment'}</Empty>}
                    </div>
                </HScroll>
            )}
        </div>
    )
}

export default UpWatching;
